import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { DashboardWidgetTypes } from '../../../enums/dashboardWidgetTypes';
import { DashboardLayoutViewModel } from '../../../viewModels/dashboard/dashboardLayoutViewModel';
import { DashboardLayoutWidgetViewModel } from '../../../viewModels/dashboard/dashboardLayoutWidgetViewModel';
import {
  TableHeader,
  DataType,
} from 'src/app/modules/shared/components/common/responsive-table/responsive-table/responsive-table.component';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';

@Component({
    selector: 'app-dashboard-widgets-and-layouts-modal',
    templateUrl: './dashboard-widgets-and-layouts-modal.component.html',
    styleUrls: ['./dashboard-widgets-and-layouts-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardWidgetsAndLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('publicTemplate') publicTemplate: TemplateRef<ElementRef<HTMLElement>>;
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<ElementRef<HTMLElement>>;

  onLayoutChanged: EventEmitter<DashboardLayoutViewModel> = new EventEmitter();
  onLayoutDeleted: EventEmitter<DashboardLayoutViewModel> = new EventEmitter();
  public layouts: DashboardLayoutViewModel[] = [];
  public layout: DashboardLayoutViewModel;
  public widgets: DashboardLayoutWidgetViewModel[] = [];
  public defaultWidgets: DashboardLayoutWidgetViewModel[] = [];
  public widgetTypes: DashboardWidgetTypes;
  public columns = 2;
  public selectedTabId: string;
  public isLayoutChanged: boolean = false;
  public readonly T = T;
  public readonly moduleTypes = ModuleTypes;
  public selectedSummaryBarWidget: DashboardLayoutWidgetViewModel;
  public summaryBarWidgets: DashboardLayoutWidgetViewModel[];

  private subscriptions = new Subscription();

  constructor(
    public bsModalRef: BsModalRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.widgets = this.copySectionsOrderArray(this.layout.dashboardWidgets);
    this.columns = this.layout.columns;
    this.tabChanged(this.selectedTabId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public tabChanged(id: string): void {
    this.selectedTabId = id;
  }

  public onClose(): void {
    return this.bsModalRef.hide();
  }

  public onSave(): void {
    const newLayout:DashboardLayoutViewModel = JSON.parse(JSON.stringify(this.layout)) as DashboardLayoutViewModel;

    newLayout.dashboardWidgets = this.widgets;
    newLayout.id = 0;
    newLayout.default = false;
    newLayout.columns = this.columns;
    newLayout.accountDefault = false;

    if (this.selectedSummaryBarWidget) {
      newLayout.summaryBarId = this.selectedSummaryBarWidget.id;
    }

    this.onLayoutChanged.next(newLayout);
    this.bsModalRef.hide();
  }

  public deleteLayout(layout: DashboardLayoutViewModel): void {
    this.layouts = this.layouts.filter((x) => x.id !== layout.id);
    this.onLayoutDeleted.next(layout);
  }

  private copySectionsOrderArray(widgets: DashboardLayoutWidgetViewModel[]) {
    let outObject, value, key;

    if (typeof widgets !== 'object' || widgets === null) {
      return widgets;
    }

    outObject = Array.isArray(widgets) ? [] : {};

    for (key in widgets) {
      value = widgets[key];
      outObject[key] = this.copySectionsOrderArray(value);
    }

    return outObject;
  }

  get tableHeaders(): TableHeader[] {
    const headers = [
      {
        dataType: DataType.String,
        title: this.translateService.instant(T.common.saved_view_title),
        property: 'title',
      },
      {
        dataType: DataType.Template,
        title: this.translateService.instant(T.common.public),
        template: this.publicTemplate,
      },
      {
        dataType: DataType.Template,
        title: this.translateService.instant(T.common.action.many),
        template: this.actionsTemplate,
      },
    ];

    return headers;
  }

  public columnsUpdated(columnNumber: number): void {
    this.isLayoutChanged = true;
    this.columns = columnNumber;
  }

  public summaryBarUpdated(selectedSummaryBarWidget: DashboardLayoutWidgetViewModel): void {
    this.isLayoutChanged = true;
    this.selectedSummaryBarWidget = selectedSummaryBarWidget;
  }

  public dashboardLayoutUpdated(widgets: DashboardLayoutWidgetViewModel[]): void {
    this.isLayoutChanged = true;
    this.widgets = widgets;
  }

  public isDashboardLayoutEmpty(): boolean {
    return this.widgets.some(x => x.isActive === true);
  }
}
